import React, { useEffect, useLayoutEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import TaskDetailCore from '../tasks/TaskDetailCore';
import { TicketTypeIcon } from '../../components/common/TicketTypeSelector';
import { Avatar } from '../../components/common/Avatar';
import api from '../../services/api';
import './AllIssues.css';

const STATUS_FALLBACK_COLORS = {
  backlog:     '#6b7280',
  in_progress: '#3b82f6',
  review:      '#f59e0b',
  done:        '#10b981',
};

const StatusPill = ({ status, workflowStatuses }) => {
  const ws = workflowStatuses.find(s => s.key === status);
  const label = ws?.name || status?.replace(/_/g, ' ') || '—';
  const color = ws?.color || STATUS_FALLBACK_COLORS[status] || '#6b7280';
  return (
    <span
      className="ai-row-status"
      style={{ background: color + '22', color, borderColor: color + '55' }}
      title={label}
    >
      {label}
    </span>
  );
};

const PAGE_SIZE = 500;

const AllIssues = ({ projectId }) => {
  const { currentProject } = useSelector(s => s.projects);

  const rootRef = useRef(null);
  const detailPanelRef = useRef(null);

  // Measure from component's top edge to viewport bottom — fills remaining height
  // without touching the global layout (which uses min-height, not height).
  useLayoutEffect(() => {
    const update = () => {
      if (!rootRef.current) return;
      const top = rootRef.current.getBoundingClientRect().top;
      rootRef.current.style.height = `${window.innerHeight - top}px`;
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTaskKey, setSelectedTaskKey] = useState(null);

  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, task: null });
  const contextMenuRef = useRef(null);

  useEffect(() => {
    if (!contextMenu.visible) return;
    const close = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(m => ({ ...m, visible: false }));
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [contextMenu.visible]);

  const handleContextMenu = (e, task) => {
    e.preventDefault();
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, task });
  };

  // Scroll detail panel to top whenever a different task is selected
  useEffect(() => {
    if (detailPanelRef.current) {
      detailPanelRef.current.scrollTop = 0;
    }
  }, [selectedTaskKey]);

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');

  const workflowStatuses = useMemo(
    () => currentProject?.workflow?.statuses || [],
    [currentProject]
  );

  const fetchAllTasks = useCallback(async () => {
    setLoading(true);
    try {
      let page = 1;
      let allTasks = [];
      while (true) {
        const res = await api.get(`/projects/${projectId}/tasks`, {
          params: { page, page_size: PAGE_SIZE },
        });
        const { tasks: pageTasks, total_pages } = res.data;
        allTasks = allTasks.concat(pageTasks || []);
        if (page >= total_pages) break;
        page += 1;
      }
      setTasks(allTasks);
    } catch (err) {
      console.error('AllIssues: failed to load tasks', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchAllTasks();
  }, [fetchAllTasks]);

  const assignees = useMemo(() => {
    const seen = new Map();
    tasks.forEach(t => {
      if (t.assignee && !seen.has(t.assignee.id)) seen.set(t.assignee.id, t.assignee);
    });
    return Array.from(seen.values());
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      if (search) {
        const q = search.toLowerCase();
        if (!t.task_key?.toLowerCase().includes(q) && !t.title?.toLowerCase().includes(q)) return false;
      }
      if (filterType && t.task_type !== filterType) return false;
      if (filterAssignee && String(t.assignee?.id) !== filterAssignee) return false;
      return true;
    });
  }, [tasks, search, filterType, filterAssignee]);

  if (loading && tasks.length === 0) {
    return (
      <div className="ai-loading">
        <span className="spinner" />
        <p>Loading issues...</p>
      </div>
    );
  }

  return (
    <div className="ai-root" ref={rootRef}>
      <div className="ai-toolbar">
        <div className="ai-search-wrap">
          <svg className="ai-search-icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
          </svg>
          <input
            className="ai-search"
            type="text"
            placeholder="Search by key or title..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="ai-search-clear" onClick={() => setSearch('')}>x</button>
          )}
        </div>

        <select className="ai-filter-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All types</option>
          <option value="epic">Epic</option>
          <option value="story">Story</option>
          <option value="task">Task</option>
          <option value="bug">Bug</option>
          <option value="subtask">Subtask</option>
        </select>

        <select className="ai-filter-select" value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
          <option value="">All assignees</option>
          {assignees.map(a => (
            <option key={a.id} value={a.id}>{a.full_name}</option>
          ))}
        </select>
      </div>

      <div className="ai-body">
        <div className="ai-list-panel">
          <div className="ai-list-header">
            {filteredTasks.length !== tasks.length
              ? `${filteredTasks.length} of ${tasks.length} issue${tasks.length !== 1 ? 's' : ''}`
              : `${tasks.length} issue${tasks.length !== 1 ? 's' : ''}`}
          </div>
          {filteredTasks.length === 0 ? (
            <div className="ai-list-empty">
              {tasks.length === 0 ? 'No issues found.' : 'No issues match the current filters.'}
            </div>
          ) : (
            filteredTasks.map(task => (
              <div
                key={task.id}
                className={`ai-card${selectedTaskKey === task.task_key ? ' ai-card--selected' : ''}`}
                onClick={() => setSelectedTaskKey(task.task_key)}
                onContextMenu={(e) => handleContextMenu(e, task)}
              >
                <span className="ai-card-title">{task.title}</span>
                <div className="ai-card-meta">
                  <div className="ai-card-meta-left">
                    <TicketTypeIcon type={task.task_type} size="sm" />
                    <span className="ai-card-key">{task.task_key}</span>
                  </div>
                  {task.assignee ? (
                    <Avatar user={task.assignee} size={22} />
                  ) : (
                    <span className="ai-card-avatar-placeholder" />
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="ai-detail-panel" ref={detailPanelRef}>
          {selectedTaskKey ? (
            <TaskDetailCore
              identifier={selectedTaskKey}
              identifierType="key"
              renderMode="sidepanel"
              onClose={() => setSelectedTaskKey(null)}
              onDeleted={() => setSelectedTaskKey(null)}
            />
          ) : (
            <div className="ai-empty-state">
              <svg
                className="ai-empty-state-icon"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18" />
                <path d="M9 21V9" />
              </svg>
              <span className="ai-empty-state-text">Select an issue to view its details</span>
            </div>
          )}
        </div>
      </div>
      {contextMenu.visible && contextMenu.task && (
        <div
          ref={contextMenuRef}
          className="task-ctx-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="task-ctx-item"
            onClick={() => {
              window.open(`/tasks/${contextMenu.task.task_key}`, '_blank');
              setContextMenu(m => ({ ...m, visible: false }));
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            Open in new tab
          </button>
        </div>
      )}
    </div>
  );
};

export default AllIssues;
